import { collection, query, where, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../config';
import { getDocsOnline } from './getDocsOnline';
import type { FirestorePlaceDoc } from '../../types/place';

const COLLECTION_NAME = 'stations';

/**
 * Find all published stations in a space, ordered.
 */
export async function findStationsBySpace(spaceId: string): Promise<FirestorePlaceDoc[]> {
  const q = query(
    collection(db!, COLLECTION_NAME),
    where('spaceId', '==', spaceId),
    where('status', '==', 'published'),
    orderBy('order')
  );
  const snapshot = await getDocsOnline(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as FirestorePlaceDoc));
}

/**
 * Get a single station by ID.
 * Replaces: getPlace(id) (directus.js)
 */
export async function findStationById(stationId: string): Promise<FirestorePlaceDoc | null> {
  const docRef = doc(db!, COLLECTION_NAME, stationId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as FirestorePlaceDoc;
}

/**
 * Find all stations with a location (for map display).
 */
export async function findStationsWithLocation(siteId: string): Promise<FirestorePlaceDoc[]> {
  const q = query(
    collection(db!, COLLECTION_NAME),
    where('appId', '==', siteId),
    where('status', '==', 'published')
  );
  const snapshot = await getDocsOnline(q);
  return (snapshot.docs
    .map(d => ({ id: d.id, ...d.data() } as FirestorePlaceDoc)))
    .filter(station => station.location && station.location.lat !== 0)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Find a station by its keypad display number.
 */
export async function findStationByKeypad(
  siteId: string,
  displayNumber: string | number
): Promise<FirestorePlaceDoc | null> {
  const q = query(
    collection(db!, COLLECTION_NAME),
    where('appId', '==', siteId),
    where('identifiers.displayNumber', '==', String(displayNumber))
  );
  const snapshot = await getDocsOnline(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as FirestorePlaceDoc;
}
